import React from 'react';
import { Card } from '../../../components/base/Card';

export const VisionSection: React.FC = () => {
  return (
    <section id="vision" className="py-24 bg-gradient-to-b from-brand-indigo via-brand-indigo/80 to-brand-indigo relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-aurora-blue/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-aurora-violet/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-off-white mb-8 leading-tight">
            The Origin of 
            <span className="block text-transparent bg-clip-text bg-aurora-gradient">True Intelligence</span>
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-brand-teal to-aurora-green mx-auto rounded-full mb-8"></div>
        </div>

        {/* Mission Statement */}
        <div className="text-center mb-16">
          <div className="max-w-4xl mx-auto">
            <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed mb-8">
              Whence is the origin point of true intelligence. Today's AI forgets; Whence remembers. 
              By tying every conversation, every decision, back to its origin, Whence transforms AI 
              from mimicry into memory, from parrots into partners.
            </p>
            <p className="text-lg text-brand-teal font-semibold">
              Whence is the missing key — the bridge from narrow AI to AGI.
            </p>
          </div>
        </div>

        {/* Key principles grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card variant="glass" className="p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 mx-auto mb-6 bg-aurora-blue/20 rounded-full flex items-center justify-center">
              <i className="ri-brain-line text-2xl text-aurora-blue"></i>
            </div>
            <h3 className="text-xl font-bold text-brand-off-white mb-4">Memory & Context</h3>
            <p className="text-gray-300 leading-relaxed">
              Every interaction builds upon the last, creating true understanding through persistent memory and contextual awareness.
            </p>
          </Card>

          <Card variant="glass" className="p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 mx-auto mb-6 bg-aurora-violet/20 rounded-full flex items-center justify-center">
              <i className="ri-links-line text-2xl text-aurora-violet"></i>
            </div>
            <h3 className="text-xl font-bold text-brand-off-white mb-4">Continuity</h3>
            <p className="text-gray-300 leading-relaxed">
              Seamless connections across conversations, sessions, and experiences create an unbroken chain of intelligent interaction.
            </p>
          </Card>

          <Card variant="glass" className="p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 mx-auto mb-6 bg-aurora-green/20 rounded-full flex items-center justify-center">
              <i className="ri-rocket-line text-2xl text-aurora-green"></i>
            </div>
            <h3 className="text-xl font-bold text-brand-off-white mb-4">Evolution</h3>
            <p className="text-gray-300 leading-relaxed">
              Systems that learn, adapt, and grow—not just process—creating intelligence that evolves with every interaction.
            </p>
          </Card>
        </div>

        {/* Vision statement */}
        <Card variant="gradient" className="p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-20 h-20 mx-auto mb-8 bg-brand-teal/30 rounded-full flex items-center justify-center animate-glow">
              <i className="ri-eye-line text-3xl text-brand-teal"></i>
            </div>
            <h3 className="text-3xl font-bold text-brand-off-white mb-6">Our Vision</h3>
            <p className="text-xl text-gray-300 leading-relaxed mb-6">
              We envision a world where artificial intelligence doesn't just respond—it remembers, learns, and grows. 
              Where every AI interaction builds upon a foundation of understanding, creating partners in thought rather than tools for tasks.
            </p>
            <p className="text-lg text-brand-teal font-semibold">
              This is the future Whence is building. This is where intelligence begins.
            </p>
          </div>
        </Card>
      </div>
    </section>
  );
};
